import { FaroBetaBadge } from "@components/common/faro-beta-badge";
import { FaroTextButton } from "@components/common/faro-text-button";
import { ExtendedTabProps } from "@components/common/tabs/tabs-type";
import { SendFeedbackDialog } from "@components/header/send-feedback-dialog";
import { SdbProject } from "@custom-types/project-types";
import { blue, neutral, Popover } from "@flat-ui";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { Box, BoxProps, Grid2, Stack, Tab, Tabs } from "@mui/material";
import { RouteParams } from "@router/route-params";
import { getPreservedQueryParams, useAppParams } from "@router/router-helper";
import { SecuredRoute } from "@router/secured-route";
import { TEAMS_DISPLAY_NAME } from "@src/constants/team-constants";
import {
  DEFAULT_TAB_UNDERLINE,
  TAB_WITH_NO_UNDERLINE,
} from "@styles/common-styles";
import { OpenTabEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";

interface Props<K, RequiredT> {
  /** The tab that is selected */
  selectedTab: K;

  /** Data needed for each tab */
  tabs: ExtendedTabProps<K, RequiredT>[];

  /**
   * the value used to identify each Nav link of the sideBar by a unique id
   * this value is used for our automation framework playwright
   */
  dataTestId?: string;

  /** The selected project (if applies), useful to identify the user's permission levels */
  selectedProject?: SdbProject | null;

  /**
   * Defines a required attribute that it is needed for the tab to be loaded.
   * While this attribute is undefined or null, the tab will show the loading content.
   */
  requiredAttribute: RequiredT;

  /** Whether the data required to display the tabs is loading */
  isLoading?: boolean;

  /**
   * The name of the event to track opening tab events.
   * In the same page all opening of a tab is tracked with the same event name,
   * and the tab name is sent as a prop.
   */
  trackingEventPageName: OpenTabEvents;
}

/**
 * A general component which generates tabs that are changing the routes
 */
export function TabsWithRoutes<
  T extends keyof RouteParams,
  K extends RouteParams[T],
  RequiredT
>({
  selectedTab,
  tabs,
  dataTestId,
  trackingEventPageName,
  selectedProject = null,
  requiredAttribute,
  isLoading = false,
}: Props<K, RequiredT>): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const { memberId } = useAppParams();
  const { hasUserPermissionCompanyLevel } = useHasUserValidRoleCompanyLevel();
  const { hasUserPermissionProjectLevel } =
    useHasUserValidPermissionProjectLevel();

  const popoverRefs = useRef<{
    [key: string]: HTMLDivElement | HTMLAnchorElement | null;
  }>({});

  const [activePopoverIndex, setActivePopoverIndex] = useState<number | null>(
    null
  );
  const [shouldShowGiveFeedback, setShouldShowGiveFeedback] =
    useState<boolean>(false);

  // Get the current location and preserved query parameters
  const location = useLocation();
  const preservedSearch = getPreservedQueryParams(location.search);

  // Returns the content of the selected tab
  const selectedTabContent = useMemo(() => {
    const tabToShow = tabs.find(({ route }) => route === selectedTab);

    /**
     * Checks whether the required attribute is defined and not null.
     */
    function isRequiredAttributeDefined<RequiredT>(
      requiredAttribute: RequiredT
    ): requiredAttribute is Exclude<RequiredT, undefined | null> {
      return requiredAttribute !== undefined && requiredAttribute !== null;
    }

    /** Show the loading content while the required attribute is not defined yet or if the data is loading */
    if (!isRequiredAttributeDefined(requiredAttribute) || isLoading) {
      return tabToShow?.loadingContent;
    }

    /**
     * If the tab is still loading its own data (isTabLoading), show loading content.
     * This prevents redirecting to "Access denied" when the tab is accessed via deeplink
     * while its data is still being fetched.
     */
    if (tabToShow?.isTabLoading) {
      return tabToShow?.loadingContent;
    }

    return (
      <SecuredRoute
        requiredRoleCompanyLevel={tabToShow?.requiredRoleCompanyLevel}
        requiredFeatureCompanyLevel={tabToShow?.requiredFeatureCompanyLevel}
        requiredPermissionProjectLevel={
          tabToShow?.requiredPermissionProjectLevel
        }
        isHidden={tabToShow?.isHidden}
        loadingContent={tabToShow?.loadingContent}
      >
        {tabToShow?.content(requiredAttribute)}
      </SecuredRoute>
    );
  }, [tabs, requiredAttribute, isLoading, selectedTab]);

  // Returns the action buttons of the selected tab
  const selectedTabActionButtons = useMemo(() => {
    return tabs.find(({ route }) => route === selectedTab)?.actionButtons;
  }, [tabs, selectedTab]);

  /**
   * Returns whether a tab is hidden based on the disabled property
   * or on the user's permissions if provided.
   *
   * @returns True if the tab should be hidden.
   */
  const isTabHidden = useCallback(
    (tab: ExtendedTabProps<K, RequiredT>): boolean => {
      // If the tab is still loading, hide it from the tab list.
      // This is checked before the selectedTab check because we want to hide
      // loading tabs even if they are selected (accessed via deeplink).
      // The SecuredRoute will show loading content instead of redirecting.
      if (tab.isTabLoading) {
        return true;
      }

      // Never hide the selected tab because it was probably open from a deep link.
      // Otherwise there is an error thrown in the console that the route does not exist.
      // If user does not have access to the tab it will be redirected to the forbidden page
      // by the secured route component.
      if (selectedTab === tab.route) {
        return false;
      }

      if (tab.isHidden) {
        return true;
      }

      if (
        tab.requiredRoleCompanyLevel &&
        !hasUserPermissionCompanyLevel({
          roleName: tab.requiredRoleCompanyLevel,
          memberId,
        })
      ) {
        return true;
      }

      if (
        tab.requiredPermissionProjectLevel &&
        !hasUserPermissionProjectLevel({
          permissionName: tab.requiredPermissionProjectLevel,
          project: selectedProject,
        })
      ) {
        return true;
      }
      return false;
    },
    [
      selectedTab,
      hasUserPermissionCompanyLevel,
      hasUserPermissionProjectLevel,
      memberId,
      selectedProject,
    ]
  );

  // Filter out hidden tabs
  const visibleTabs = useMemo(() => {
    return tabs.filter((tab) => tab && !isTabHidden(tab));
  }, [tabs, isTabHidden]);

  return (
    <Box component="div" sx={{ width: "100%" }} data-testid={dataTestId}>
      <Box
        component="div"
        sx={{
          width: "100%",
          background: neutral[0],
          paddingTop: 2,
        }}
      >
        <Tabs
          value={selectedTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            "& .MuiTabs-scrollButtons": {
              opacity: 0.8,
              "&.Mui-disabled": {
                display: "none",
              },
            },
            "& .MuiTabs-flexContainer": {
              gap: { xs: 1, sm: 0 },
            },
          }}
          component={GetTabTitlesWithActions}
          selectedTabActionButtons={selectedTabActionButtons}
          slotProps={{
            indicator: {
              sx: {
                backgroundColor: blue[500],
                height: "3px",
              },
            },
          }}
        >
          {visibleTabs.map((tab, index) => (
            <Tab
              ref={(el) => {
                popoverRefs.current[index] = el;
              }}
              onMouseEnter={() => {
                if (tab.popover) {
                  setActivePopoverIndex(index);
                }
              }}
              onMouseLeave={() => {
                if (tab.popover) {
                  setActivePopoverIndex(null);
                }
              }}
              disableRipple
              draggable={false}
              key={tab.route}
              component={Link}
              label={
                <Stack direction="row" spacing={1}>
                  {tab.label}
                  {tab.isBetaFeature && (
                    <FaroBetaBadge sx={{ bottom: "10px" }} />
                  )}
                  {tab.isNewFeature && (
                    <FaroBetaBadge sx={{ bottom: "10px" }} text="NEW" />
                  )}
                  {tab.popover && (
                    <Popover
                      dark
                      open={activePopoverIndex === index}
                      anchorEl={popoverRefs.current[index]}
                      onClose={() => setActivePopoverIndex(null)}
                      closeOnClickOutside={false}
                      title={tab.popover.title}
                      description={
                        <>
                          {tab.popover.description}
                          {tab.popover.shouldShowFeedbackButton && (
                            <Stack alignItems={"flex-end"}>
                              <FaroTextButton
                                onClick={() => {
                                  setShouldShowGiveFeedback(true);
                                  setActivePopoverIndex(null);
                                }}
                                sx={{
                                  fontWeight: "bold",
                                  "&:focus": {
                                    backgroundColor: "transparent",
                                  },
                                }}
                              >
                                Give feedback
                              </FaroTextButton>
                            </Stack>
                          )}
                        </>
                      }
                    />
                  )}
                </Stack>
              }
              value={tab.route}
              relative="path"
              to={{
                pathname: `../${tab.route}`,
                search: preservedSearch,
              }}
              sx={{
                padding: 0,
                fontSize: "14px",
                letterSpacing: "-0.2px",
                marginRight: { xs: "6px", sm: "20px" },
                textTransform: "none",
                minWidth: { xs: "auto", sm: "30px" },
                fontWeight: selectedTab === tab.route ? "bold" : "lighter",
                "&.MuiTab-root": {
                  color: selectedTab === tab.route ? blue[500] : neutral[800],
                },
                "&:hover": {
                  color: blue[500],
                  borderBottom: `1px solid ${blue[500]}`,
                  // Add a top padding to avoid the tab content from jumping
                  paddingTop: "1px",
                },
              }}
              onClick={() => {
                trackEvent({
                  name: trackingEventPageName,
                  props: {
                    tab: getTrackingTabName(tab.label, tab.trackingTabName),
                  },
                });
              }}
            />
          ))}
        </Tabs>
      </Box>
      {selectedTabContent}

      {/** This dialog is used for submitting feedback about teams (user groups) */}
      <SendFeedbackDialog
        isDialogOpen={shouldShowGiveFeedback}
        onClose={() => {
          setShouldShowGiveFeedback(false);
          setActivePopoverIndex(null);
        }}
        title="How can we improve teams?"
        featureName={`${TEAMS_DISPLAY_NAME} (User Groups)`}
        confirmText="Submit"
      />
    </Box>
  );
}

interface GetTabTitlesWithActionsProps {
  /** List of action buttons to show for specific tab */
  selectedTabActionButtons?: JSX.Element;

  /** The props of the Box properties that is passed from TabsWithRoutes directly */
  props?: BoxProps;
}

const HEIGHT_OF_COMPONENT_WITHOUT_BREAKS = 52;

/**
 * Add action buttons at the right of a specific tab
 */
function GetTabTitlesWithActions({
  selectedTabActionButtons,
  ...props
}: GetTabTitlesWithActionsProps): JSX.Element {
  const [isWrapped, setIsWrapped] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * This useEffect initializes a ResizeObserver to dynamically adjust the layout of the tab titles and action
   * buttons based on the container's height. If the height exceeds a predefined threshold
   * (HEIGHT_OF_COMPONENT_WITHOUT_BREAKS), it indicates that the buttons have wrapped onto a new line, prompting CSS
   * adjustments to ensure proper spacing and styling. The observer is cleaned up when the component
   * unmounts to prevent memory leaks.
   */
  useEffect(() => {
    // Initialize ResizeObserver to monitor changes in the container's size
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      // Check if the container's height exceeds the threshold indicating a line break
      const isBreakingLines =
        entries[0].target.clientHeight > HEIGHT_OF_COMPONENT_WITHOUT_BREAKS;
      setIsWrapped(isBreakingLines);
    });

    observer.observe(container);

    return () => {
      observer.unobserve(container);
    };
  }, []);

  return (
    <Grid2 container ref={containerRef}>
      <Grid2
        sx={{
          boxShadow: DEFAULT_TAB_UNDERLINE,
          flexGrow: 1,
        }}
        {...{ ...props, component: "div" }}
      />

      <Grid2
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "end",
          flexGrow: 1,
          mt: isWrapped ? "10px" : 0,
          boxShadow: isWrapped ? TAB_WITH_NO_UNDERLINE : DEFAULT_TAB_UNDERLINE,
        }}
      >
        {selectedTabActionButtons}
      </Grid2>
    </Grid2>
  );
}

/** Returns the name of a tab for event-tracking based on its trackingTabName or if not provided the label */
function getTrackingTabName(
  label: ReactNode,
  trackingTabName?: string
): string {
  if (trackingTabName) {
    return trackingTabName;
  }

  return typeof label === "string" ? label : "";
}
